import type { ReactNode } from 'react';
import { useRoomContext } from '@livekit/components-react';
import { CompositorCanvas } from '@liveforge/compositor';
import type { LayerTree } from '@liveforge/shared';
import { DetachIcon } from './icons';

export interface MonitorProps {
  layers: LayerTree;
  /** "Preview" or "Program". */
  label: string;
  /** Marks this monitor as what the audience is seeing. */
  onAir?: boolean;
  /** Layout channel state. Only meaningful on the programme monitor. */
  synced?: boolean;
  /** Why the channel is down, shown on the dot rather than only as a colour. */
  syncError?: string | null;
  /**
   * Drawn over the composition, exactly on the frame rather than on the box
   * around it - which is why the canvas has a wrapper of its own.
   */
  overlay?: ReactNode;
  /** Only the director's preview marks what has no source yet. */
  showPlaceholders?: boolean;
  /**
   * Opens this composition in a window of its own. Only the programme has one:
   * it is the real Layout Page, rendering from the relay rather than from this
   * browser's store, which is what makes it worth looking at.
   */
  onDetach?: () => void;
  /** Disables the detach button while its token is being minted. */
  detaching?: boolean;
}

/**
 * One monitor: what a composition looks like, at broadcast proportions.
 *
 * This is the *same* component the Layout Page mounts for Egress, drawing the
 * same layer tree from the same code - so the preview is not an approximation
 * of the output, it is the output at a smaller display size.
 *
 * It deliberately reuses the studio's existing room rather than opening its
 * own connection. An iframe would have been the other way to guarantee
 * parity, but it would join the room a second time: another subscriber, every
 * feed downloaded twice, and a phantom participant in everyone's list.
 */
export function Monitor({
  layers,
  label,
  onAir = false,
  synced,
  syncError,
  overlay,
  showPlaceholders = false,
  onDetach,
  detaching = false,
}: MonitorProps) {
  const room = useRoomContext();

  return (
    <section className={`monitor ${onAir ? 'monitor--on-air' : 'monitor--preview'}`}>
      <div className="monitor__header">
        <h2>{label}</h2>
        {onAir ? (
          <span className="monitor__tally">ON AIR</span>
        ) : (
          <span className="monitor__tally monitor__tally--preview">STAGED</span>
        )}
        {synced === undefined ? null : (
          <span
            className={`sync-dot ${synced ? 'sync-dot--on' : 'sync-dot--off'}`}
            title={
              synced ? 'Layout channel connected' : (syncError ?? 'Layout channel disconnected')
            }
          />
        )}
        {onDetach ? (
          <button
            type="button"
            className="icon-button monitor__detach"
            onClick={onDetach}
            disabled={detaching}
            title="Open the program in its own window - the real layout page, as Egress will load it"
            aria-label="Open the program in its own window"
          >
            <DetachIcon />
          </button>
        ) : null}
      </div>
      <div className="monitor__frame">
        <div className="monitor__stage">
          {/*
          Composed at half the broadcast size and capped at 30fps. The panel is
          a few hundred pixels wide, so nothing is visibly lost, and the studio
          stops paying Egress's per-frame cost while the director is also
          encoding and uploading their own cameras.
        */}
          <CompositorCanvas
            room={room}
            layers={layers}
            className="monitor__canvas"
            renderWidth={960}
            renderHeight={540}
            maxFps={30}
            showPlaceholders={showPlaceholders}
          />
          {overlay}
        </div>
      </div>
    </section>
  );
}
